<template>
  <div>
    <div class="icon-container">

      <i class="fa-solid fa-circle text-success client-action-icon" v-if="client.online" v-b-tooltip.hover title="online"></i>
      <i class="fa-solid fa-circle text-danger client-action-icon" v-else v-b-tooltip.hover title="offline"></i>

      <!-- NOTES: hover to fetch & show (same UI) -->
      <span class="icon" @click="handleClickNote" @mouseleave="handleMouseLeave">
        <i :id="popoverId" class="fa fa-note-sticky" style="color: #dca20e; cursor: pointer"></i>
      </span>


      <span class="icon" @click.stop="openClientMonetary" @click.ctrl="openClientNewTab" @mousedown.middle.prevent="openClientNewTab">
        <i class="fa fa-credit-card client-action-icon" style="color: white; text-shadow: -1px -1px 0px black, 1px -1px 0px black, -1px 1px 0px black, 1px 1px 0px black;"></i>
      </span>

      <a v-if="hasPermission(permissions.CLIENT_READ_PHONE)"
          class="icon"
          :href="'https://api.whatsapp.com/send?phone=' + encodeURIComponent(client.phone)"
          target="_blank"
          rel="noopener"
          @click.stop
      >
        <i class="fab fa-whatsapp client-action-icon" style="color: #4CAF50"></i>
      </a>

      <!-- Click2Call: one click rings the agent's extension, then dials the client -->
      <span class="icon" v-if="hasPermission(permissions.CLIENT_READ_PHONE) && hasCallablePhone"
            @click.stop="startCall" v-b-tooltip.hover :title="$t('click2call.initiate')">
        <b-spinner v-if="calling" small style="width: 12px; height: 12px;"/>
        <i v-else class="fa-solid fa-phone-volume client-action-icon" style="color: #7367f0"></i>
      </span>

      <router-link v-if="hasPermission(permissions.CLIENT_READ_PHONE)"
         class="icon" :to="{ path: '/clients/chat', query: { sessionClientId: client.id } }"
      >
        <i class="fa-regular fa-comment"></i>
      </router-link>

      <b-badge @click.stop="openClientMonetary" @click.ctrl="openClientNewTab" @mousedown.middle.prevent="openClientNewTab" :variant="client.isTest ? 'warning' :'primary' ">
        {{ client.firstName }} {{ client.lastName }}
      </b-badge>
    </div>

    <!-- Popover mounted to body, shown/hidden via root events -->
    <b-popover
        :target="popoverId"
        triggers="manual"
        placement="auto"
        container="body"
        boundary="window"
        custom-class="notes-popover"
    >
      <template #title>Notes</template>
      <div v-html="popoverContent"></div>
    </b-popover>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinPermissions from '@/components/mixin/mixinPermissions';

export default {
  name: 'ClientCell',
  mixins: [mixinBase, mixinPermissions],
  props: {
    client: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      popoverContent: 'Loading...',
      isFetchingNotes: false,
      notesLoadedOnce: false,
      showNotes: false,
      cancelHover: false, // to handle fast leave
      calling: false,
    };
  },
  computed: {
    // Stable per-row id so the popover can always find its target
    popoverId() {
      return `client-notes-${this.client.id}`;
    },
    hasCallablePhone() {
      const phone = this.client.phone || this.client.mobile;
      return phone && String(phone).trim().length >= 6;
    },
  },
  methods: {
    ...mapActions('notes', ['getNotes']),
    ...mapActions('clients', ['click2call']),

    startCall() {
      if (this.calling) return;
      const phone = this.client.phone || this.client.mobile;
      if (!phone) return;
      this.calling = true;
      this.click2call({ phoneNumber: phone, clientId: this.client.id })
          .then(() => {
            this.$bvToast.toast(this.$t('click2call.success'), {
              title: this.$t('click2call.confirmTitle'), variant: 'success', solid: true,
            });
          })
          .catch(err => {
            const desc = err && err.response && err.response.data && err.response.data.params
                ? err.response.data.params[0] : (err && err.message) || '';
            this.$bvToast.toast(`${this.$t('click2call.error')}${desc ? ': ' + desc : ''}`, {
              title: this.$t('click2call.confirmTitle'), variant: 'danger', solid: true, autoHideDelay: 8000,
            });
          })
          .finally(() => { this.calling = false; });
    },

    openClientNewTab(){
      const url = `/client/${this.client.id}`;
      window.open(url, '_blank');
    },
    openClientMonetary(ev) {
      if (ev && ev.ctrlKey) return;
      this.showClient({
        id: this.client.id,
        tab: 'monetary'
      });
    },


    async handleClickNote() {
      // If already visible, toggle off
      if (this.showNotes) {
        this.hideNotes();
        return;
      }

      this.showNotes = true;
      this.cancelHover = false;
      this.$root.$emit('bv::show::popover', this.popoverId);

      // Call your async loader
      await this.onHoverNotes();
    },

    async onHoverNotes() {
      if (this.notesLoadedOnce || this.isFetchingNotes) return;

      this.isFetchingNotes = true;
      this.popoverContent = 'Loading...';

      try {
        const noteData = await this.getNotes({
          clientId: this.client.id,
          pagination: {
            page: 1,
            size: 999
          }
        });

        // If mouse already left, abort showing
        if (this.cancelHover) return;

        const notes = Array.isArray(noteData.data) ? noteData.data : [];
        if (notes.length) {
          this.popoverContent = notes
              .map(n => {
                const d = new Date(n.time).toLocaleDateString();
                const text = (n.text || '').replace(/\n/g, '<br>');
                return `<div style="text-align:center; font-weight:600;">[${d}]</div><div>${text}</div>`;
              })
              .join('<hr style="margin:8px 0; opacity:.25;">');
        } else {
          this.popoverContent = 'No notes available';
        }
        this.notesLoadedOnce = true;
      } catch (e) {
        console.error('Error fetching notes:', e);
        this.popoverContent = 'Failed to load notes';
      } finally {
        this.isFetchingNotes = false;
      }
    },

    handleMouseLeave() {
      // Mark cancellation and close immediately
      this.cancelHover = true;
      this.hideNotes();
    },

    hideNotes() {
      this.showNotes = false;
      this.$root.$emit('bv::hide::popover', this.popoverId);
    }

  }
};
</script>

<style lang="scss">
.icon-container {
  display: flex;
  gap: 10px;
  align-items: center;

  .icon {
    cursor: pointer;
    font-size: 1.5rem;
    color: #6c757d;
    transition: color 0.2s ease-in-out;

    &:hover {
      transform: scale(1.3);
      transition: all 0.2s ease-in-out;
    }
  }
}

/* Optional: nicer popover styling */
.notes-popover .popover-body {
  max-width: 320px;
  font-size: 0.875rem;
  line-height: 1.2rem;
}

.notes-popover .popover-header {
  font-weight: 600;
}
</style>
